/**
 * Two-cadence refresh scheduler for the /viewer page (§8.6 follow-up, owner
 * test find 2026-07-21: the original live-only 5s loop left a parked page
 * blind to a poll created after load).
 *
 * - LIVE (`setLive(true)` — an event is known): full `refresh()` every
 *   `liveMs`, so tallies/lock/results track near-live.
 * - IDLE: `probe()` (the skeletal /api/viewer/polls-open fetch) every
 *   `idleMs`; a probe reporting an open event promotes straight to ONE
 *   `refresh()` — the refreshed state then flips `setLive` from the
 *   component, which switches the cadence.
 * - Visibility: exactly ONE timer slot exists, and it is armed only while
 *   the document is visible. Arming READS visibility (a page opened in a
 *   background tab starts PAUSED — `visibilitychange` only fires on
 *   transitions, so an initial listener alone would poll forever).
 *   Regaining visibility fires one immediate catch-up `refresh()` and
 *   restarts the cadence from zero, so the catch-up and the next tick can
 *   never coincide into a double fetch.
 *
 * Module format: CommonJS with the framework-agnostic state machine in
 * startRefreshScheduler() — the use-visible-polling.js recipe — so jest
 * exercises the cadence/visibility behavior with fake timers and a stub
 * document, no DOM or React renderer (test-review 2026-07-21: two cadences
 * with promotion are strictly more intricate than the admin hook this
 * mirrors, and source pins cannot catch timer-lifecycle bugs).
 *
 * @module app/viewer/refresh-scheduler
 */

'use strict';

const { useEffect, useRef } = require('react');

/**
 * Start the scheduler. `refresh` and `probe` MUST be stable references.
 *
 * @param {() => void} refresh - full-state re-fetch (fire-and-forget)
 * @param {() => Promise<boolean>} probe - "is any event live?" (a rejection
 *   is swallowed — a discovery blip must never surface; the next tick retries)
 * @param {number} liveMs
 * @param {number} idleMs
 * @param {Document} [doc] - injectable for tests; defaults to the global document
 * @returns {{ setLive: (live: boolean) => void, stop: () => void }}
 */
function startRefreshScheduler({ refresh, probe, liveMs, idleMs, doc = document }) {
  let live = false;
  let timer = null;
  let stopped = false;
  const visible = () => doc.visibilityState !== 'hidden';
  const probeTick = async () => {
    try {
      if (await probe()) refresh();
    } catch {
      // Transient network failure — the next probe retries.
    }
  };
  const arm = () => {
    clearInterval(timer);
    timer = null;
    if (stopped || !visible()) return;
    timer = setInterval(live ? refresh : probeTick, live ? liveMs : idleMs);
  };
  const onVisibility = () => {
    if (stopped) return;
    // Catch up immediately — the viewer may have missed a poll start, a
    // result, or a balance change while the tab was hidden.
    if (visible()) refresh();
    arm();
  };
  doc.addEventListener('visibilitychange', onVisibility);
  arm();
  return {
    setLive(next) {
      const v = !!next;
      if (stopped || live === v) return;
      live = v;
      arm();
    },
    stop() {
      stopped = true;
      clearInterval(timer);
      timer = null;
      doc.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

/**
 * React wrapper: one scheduler per mount, `live` flips through setLive
 * without tearing the machine down. `refresh` and `probe` must be stable
 * across renders (useCallback) — they are effect dependencies.
 *
 * @param {{ refresh: () => void, probe: () => Promise<boolean>,
 *          live: boolean, liveMs: number, idleMs: number }} opts
 */
function useRefreshScheduler({ refresh, probe, live, liveMs, idleMs }) {
  const schedulerRef = useRef(null);
  useEffect(() => {
    const scheduler = startRefreshScheduler({ refresh, probe, liveMs, idleMs });
    schedulerRef.current = scheduler;
    return () => {
      scheduler.stop();
      schedulerRef.current = null;
    };
  }, [refresh, probe, liveMs, idleMs]);
  useEffect(() => {
    schedulerRef.current?.setLive(live);
  }, [live]);
}

module.exports = { useRefreshScheduler, startRefreshScheduler };
